import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import type { LegacyPermissionKey } from '@/config/legacyPermissions';
import { useLegacyPermissions } from '@/lib/useLegacyPermissions';
import { navigateToLegacyVendorRoute } from '@/lib/legacyNavigation';
import { getLegacyWindow } from '@/lib/legacyContext';
import { startNewCaseSession } from '@/features/caseManagement/NewCaseSession';

const SearchWidget = lazy(() => import('@/features/search/SearchWidget'));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type CaseManagementNavItem = {
  label: string;
  path: string;
  permission?: LegacyPermissionKey;
  isActive?: () => boolean;
  startsNewCase?: boolean;
};

export const caseManagementNavItems: CaseManagementNavItem[] = [
  {
    label: 'New Complaint',
    startsNewCase: true,
    path: '/CaseManagement/Complaint',
    permission: 'canAccessComplaintIntake',
  },
  {
    label: 'New Case',
    startsNewCase: true,
    path: '/CaseManagement/case/new',
    permission: 'canAccessComplaintIntake',
  },
  {
    label: 'Manager Overview',
    path: '/CaseManagement/ManagerOverview',
    permission: 'canAccessComplaintManagerOverview',
  },
];

/**
 * Mirrors AngularJS `orders.isActive(path)`:
 *   `$location.path().indexOf(path) === 0`
 *
 * `window.location.hash` is `#/Some/Path` — strip the leading `#` to get the
 * path portion comparable to `$location.path()`.
 */
function isActive(path: string): boolean {
  const hash = window.location.hash.replace(/^#/, '').split('?')[0];
  return hash.indexOf(path) === 0;
}

/**
 * Fetches unread message count via the React-native API.
 *
 * Port of AngularJS textService.updateMessageCount() which calls
 * generalDataService.searchMessages(currentUser, null, null, null, 0, ..., true).
 * The AngularJS service stored the count at `textService.messageCount` —
 * callers that previously read that singleton now use this hook instead.
 */
async function fetchMessageCountFromApi(): Promise<number> {
  try {
    const { searchMessages } = await import('@/features/messageDashboard/messageDashboardApi');
    const currentUser = localStorage.getItem('currentUser') ?? '';
    if (!currentUser) return 0;
    const result = await searchMessages({
      to: currentUser,
      from: null,
      startDate: null,
      endDate: null,
      status: 0,
      orderNumber: null,
      propertyId: null,
      pageInfo: { page: 1, orderBy: 'messageDate', sortAsc: false, pageSize: 1000 },
      currentOnly: true,
    });
    return result.assignments?.length ?? 0;
  } catch {
    return 0;
  }
}

/**
 * Port of `$scope.clearLocalStorage` from HeaderController.
 * Clears GetSearch, responseInfo, moduleId and categoryName.
 */
function clearLocalStorage(): void {
  // Dual-write to AngularJS $rootScope if available (embedded mode); harmless
  // no-op in standalone. The keys are read by legacy controllers only.
  try {
    const win = getLegacyWindow();
    const root = win.$rootScope as Record<string, unknown> | undefined;
    if (root) {
      root.GetSearch = null;
      root.responseInfo = { responseMessage: '', status: '' };
    }
  } catch {
    // ignore — non-critical
  }
  window.localStorage.removeItem('moduleId');
  window.localStorage.removeItem('categoryName');
}

// ---------------------------------------------------------------------------
// Resolve the Components link destination — mirrors the triple ng-if in the
// AngularJS template (componentsReader → autoTextReader → componentTest).
// ---------------------------------------------------------------------------

function getComponentsLink(perms: Record<string, unknown>): string | null {
  if (perms.componentsReader) return '#/Admin/Components/ChangeSource';
  if (perms.autoTextReader) return '#/Admin/Components/AutoTextMaintenance';
  if (perms.componentTest) return '#/Admin/Components/TestRendering';
  return null;
}

function getComponentsActiveBase(perms: Record<string, unknown>): string {
  if (perms.componentsReader) return '/Admin/Components/ChangeSource';
  if (perms.autoTextReader) return '/Admin/Components/AutoTextMaintenance';
  if (perms.componentTest) return '/Admin/Components/TestRendering';
  return '/Admin/Components';
}

// ---------------------------------------------------------------------------
// Dropdown hook — click-outside closes the menu
// ---------------------------------------------------------------------------

function useDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [open]);

  const toggle = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setOpen((prev) => !prev);
  }, []);

  const openMenu = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);

  return { open, toggle, openMenu, close, ref };
}

// ---------------------------------------------------------------------------
// AppHeader
// ---------------------------------------------------------------------------

export default function AppHeader() {
  const perms = useLegacyPermissions();

  // Message count — fetch via React-native searchMessages API immediately +
  // every 30 minutes (matches AngularJS $interval pattern).
  const [messageCount, setMessageCount] = useState(0);
  useEffect(() => {
    let mounted = true;
    const refresh = () => {
      fetchMessageCountFromApi().then((count) => {
        if (mounted) setMessageCount(count);
      });
    };
    refresh();
    const id = setInterval(refresh, 1_800_000); // 30 minutes
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  // Re-read active state on hash change + refresh message count on navigation
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    function onHashChange() {
      forceUpdate((n) => n + 1);
      fetchMessageCountFromApi().then(setMessageCount);
    }
    // Custom event fired by other parts of the app (e.g. PropertyTextPanel
    // after SaveTextAssignment resolves) so the badge updates immediately
    // without waiting for the 30-minute interval or a route change.
    function onMessagesChanged() {
      fetchMessageCountFromApi().then(setMessageCount);
    }
    window.addEventListener('hashchange', onHashChange);
    window.addEventListener('messages-changed', onMessagesChanged);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('messages-changed', onMessagesChanged);
    };
  }, []);

  // Mobile collapse toggle
  const [collapsed, setCollapsed] = useState(true);

  // Dropdowns
  const caseManagementDD = useDropdown();
  const invoiceDD = useDropdown();
  const moreDD = useDropdown();
  const adminDD = useDropdown();

  // Derived permission booleans
  const isSGStaff = Boolean(perms.SGStaff);
  const showVendor = isSGStaff && Boolean(perms.vmpermission);
  const showClient = isSGStaff && (Boolean(perms.clientAdmin) || Boolean(perms.clientAdminUpdater));
  const visibleCaseManagementNavItems = caseManagementNavItems.filter(
    (item) => !item.permission || Boolean(perms[item.permission])
  );
  const showCaseManagement = visibleCaseManagementNavItems.length > 0;
  const showInvoice =
    isSGStaff &&
    (Boolean(perms.invoiceReader) ||
      Boolean(perms.invoiceOnDemand) ||
      Boolean(perms.catsubcatreader) ||
      Boolean(perms.invoiceOnDemand2));
  const showMore = isSGStaff;
  const showAdmin =
    isSGStaff &&
    (Boolean(perms.adminLink) || Boolean(perms.clientAdmin) || Boolean(perms.clientAdminUpdater));
  const showMessages = isSGStaff;

  const componentsLink = getComponentsLink(perms as unknown as Record<string, unknown>);
  const componentsActiveBase = getComponentsActiveBase(perms as unknown as Record<string, unknown>);

  // Navigation helper — clears dropdown after click
  const nav = useCallback(
    (path: string, opts?: { clearLS?: boolean }) => (e: React.MouseEvent) => {
      e.preventDefault();
      if (opts?.clearLS) clearLocalStorage();
      navigateToLegacyVendorRoute(path);
    },
    []
  );

  return (
    <nav className='navbar navbar-fixed-top' role='navigation'>
      <div className='container-fluid'>
        {/* ---- Brand + mobile toggle ---- */}
        <div className='navbar-header'>
          <button
            type='button'
            className='navbar-toggle collapsed'
            aria-expanded={!collapsed}
            aria-controls='navbar'
            onClick={() => setCollapsed((c) => !c)}
          >
            <span className='sr-only'>Toggle navigation</span>
            <span className='icon-bar' />
            <span className='icon-bar' />
            <span className='icon-bar' />
          </button>
          <a className='navbar-brand' href='#/'>
            <img src='/Content/images/safeviewlogo.png' alt='SafeView' />
          </a>
        </div>

        {/* ---- Collapsible area ---- */}
        <div id='navbar' className={`navbar-collapse ${collapsed ? 'collapse' : ''}`}>
          {/* Search widget — rendered only when permission is set */}
          {Boolean(perms.searchEnabled) && (
            <Suspense fallback={<div id='search-widget-placeholder' />}>
              <SearchWidget />
            </Suspense>
          )}

          <ul className='nav navbar-nav navbar-right'>
            {/* ---- Home ---- */}
            <li>
              <a className={isActive('/home') ? 'active' : ''} href='#/home'>
                Home
              </a>
            </li>

            {/* ---- Case Management dropdown ---- */}
            {showCaseManagement && (
              <li
                className={`dropdown app-header__case-management${caseManagementDD.open ? ' open' : ''}`}
                ref={caseManagementDD.ref}
                onMouseEnter={caseManagementDD.openMenu}
                onMouseLeave={caseManagementDD.close}
              >
                <a
                  href='#'
                  className='navddp'
                  role='button'
                  aria-haspopup='true'
                  aria-expanded={caseManagementDD.open}
                  onClick={caseManagementDD.toggle}
                  onFocus={caseManagementDD.openMenu}
                >
                  Case Management <span className='caret' />
                </a>
                <ul className='dropdown-menu clientDropdown' style={{ width: 'fit-content' }}>
                  {visibleCaseManagementNavItems.map((item) => (
                    <li key={item.path}>
                      <a
                        className={
                          (item.isActive ? item.isActive() : isActive(item.path)) ? 'active' : ''
                        }
                        href={`#${item.path}`}
                        onClick={
                          item.startsNewCase
                            ? (event) => {
                                if (
                                  event.button !== 0 || event.metaKey || event.ctrlKey ||
                                  event.shiftKey || event.altKey
                                ) return;
                                event.preventDefault();
                                startNewCaseSession(item.path);
                                caseManagementDD.close();
                              }
                            : undefined
                        }
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
            )}

            {/* ---- Vendor ---- */}
            {showVendor && (
              <li>
                <a
                  className={isActive('/VendorManagement/VendorManagement') ? 'active' : ''}
                  href='#/VendorManagement/VendorManagement'
                >
                  Vendor
                </a>
              </li>
            )}

            {/* ---- Client ---- */}
            {showClient && (
              <li>
                <a
                  className={isActive('/Admin/Clients/ClientMatrix') ? 'active' : ''}
                  href='#/Admin/Clients/ClientMatrix'
                >
                  Client
                </a>
              </li>
            )}

            {/* ---- Invoice dropdown ---- */}
            {showInvoice && (
              <li className={`dropdown${invoiceDD.open ? ' open' : ''}`} ref={invoiceDD.ref}>
                <a
                  href='#'
                  className='navddp'
                  role='button'
                  aria-haspopup='true'
                  aria-expanded={invoiceDD.open}
                  onClick={invoiceDD.toggle}
                >
                  {' '}
                  INVOICE <span className='caret' />
                </a>
                <ul className='dropdown-menu clientDropdown' style={{ width: 'fit-content' }}>
                  {Boolean(perms.invoiceReader) && (
                    <li>
                      <a
                        className={isActive('/Invoice/InvoiceSummary') ? 'active' : ''}
                        href='#/Invoice/InvoiceSummary'
                      >
                        INVOICE SUMMARY
                      </a>
                    </li>
                  )}
                  {Boolean(perms.invoiceOnDemand) && (
                    <li>
                      <a
                        className={isActive('/Invoice/InvoiceOnDemand') ? 'active' : ''}
                        href='#/Invoice/InvoiceOnDemand'
                      >
                        INVOICE ON DEMAND
                      </a>
                    </li>
                  )}
                  {Boolean(perms.invoiceOnDemand2) && (
                    <li>
                      <a
                        className={isActive('/Invoice/InvoiceOnDemand2') ? 'active' : ''}
                        href='#/Invoice/InvoiceOnDemand2'
                      >
                        INVOICE ON DEMAND2
                      </a>
                    </li>
                  )}
                  {Boolean(perms.catsubcatreader) && (
                    <li>
                      <a
                        className={isActive('/Invoice/CatSubCat/ChangeSource') ? 'active' : ''}
                        href='#/Invoice/CatSubCat/ChangeSource'
                      >
                        CatSubCat Admin
                      </a>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* ---- More dropdown ---- */}
            {showMore && (
              <li className={`dropdown${moreDD.open ? ' open' : ''}`} ref={moreDD.ref}>
                <a
                  href='#'
                  className='navddp'
                  role='button'
                  aria-haspopup='true'
                  aria-expanded={moreDD.open}
                  onClick={moreDD.toggle}
                >
                  More
                  <span className='caret' />
                </a>
                <ul className='dropdown-menu clientDropdown' style={{ width: 'fit-content' }}>
                  <li>
                    <a
                      className={isActive('/Overallowables/index') ? 'active' : ''}
                      href='#/Overallowables/index'
                    >
                      Over Allowables
                    </a>
                  </li>
                  <li>
                    <a
                      className={isActive('/Location/vendorview') ? 'active' : ''}
                      href='#/Location/vendorview'
                    >
                      Vendor View
                    </a>
                  </li>
                </ul>
              </li>
            )}

            {/* ---- Admin dropdown ---- */}
            {showAdmin && (
              <li className={`dropdown${adminDD.open ? ' open' : ''}`} ref={adminDD.ref}>
                <a
                  href='#'
                  className='navddp'
                  role='button'
                  aria-haspopup='true'
                  aria-expanded={adminDD.open}
                  onClick={adminDD.toggle}
                >
                  {' '}
                  Admin <span className='caret' />
                </a>
                <ul className='dropdown-menu clientDropdown' style={{ width: 'fit-content' }}>
                  <li>
                    <a
                      className={
                        isActive('/Admin/ClientGrouping/StateGroup/StateGroupList') ? 'active' : ''
                      }
                      href='#/Admin/ClientGrouping/StateGroup/StateGroupList'
                    >
                      Client Grouping
                    </a>
                  </li>
                  {Boolean(perms.categoriesReader) && (
                    <li>
                      <a
                        className={isActive('/Admin/Categories/Categories') ? 'active' : ''}
                        href='#/Admin/Categories/Categories'
                        onClick={nav('/Admin/Categories/Categories', {
                          clearLS: true,
                        })}
                      >
                        Categories
                      </a>
                    </li>
                  )}
                  {Boolean(perms.attributeReader) && (
                    <li>
                      <a
                        className={isActive('/Admin/Attributes/Attributes') ? 'active' : ''}
                        href='#/Admin/Attributes/Attributes'
                        onClick={nav('/Admin/Attributes/Attributes', {
                          clearLS: true,
                        })}
                      >
                        Attributes
                      </a>
                    </li>
                  )}
                  {Boolean(perms.auditReader) && (
                    <li>
                      <a
                        className={isActive('/Admin/Audit/Question/Questions') ? 'active' : ''}
                        href='#/Admin/Audit/Question/Questions'
                        onClick={nav('/Admin/Audit/Question/Questions', {
                          clearLS: true,
                        })}
                      >
                        Audit
                      </a>
                    </li>
                  )}
                  {Boolean(perms.massUpload) && (
                    <li>
                      <a
                        className={isActive('/Admin/MassUpload') ? 'active' : ''}
                        href='#/Admin/MassUpload'
                      >
                        Mass Upload
                      </a>
                    </li>
                  )}
                  {componentsLink && (
                    <li>
                      <a
                        className={isActive(componentsActiveBase) ? 'active' : ''}
                        href={componentsLink}
                      >
                        Components
                      </a>
                    </li>
                  )}
                  {Boolean(perms.pricingadminreader) && (
                    <li>
                      <a
                        className={isActive('/Admin/Pricing/ChangeSource') ? 'active' : ''}
                        href='#/Admin/Pricing/ChangeSource'
                        onClick={nav('/Admin/Pricing/ChangeSource', {
                          clearLS: true,
                        })}
                      >
                        Pricing
                      </a>
                    </li>
                  )}
                  <li>
                    <a
                      className={
                        isActive('/Admin/Scheduling/StandardSetup/StandardSetup') ? 'active' : ''
                      }
                      href='#/Admin/Scheduling/StandardSetup/StandardSetup'
                    >
                      Scheduling
                    </a>
                  </li>
                  {Boolean(perms.allowablesReader) && (
                    <li>
                      <a
                        className={isActive('/Admin/Allowables/AllowableGroup') ? 'active' : ''}
                        href='#/Admin/Allowables/AllowableGroup/AllowableGroupList'
                      >
                        Allowables
                      </a>
                    </li>
                  )}
                  {Boolean(perms.promptLibrary) && (
                    <li>
                      <a
                        className={isActive('/Admin/PromptLibrary') ? 'active' : ''}
                        href='#/Admin/PromptLibrary'
                      >
                        Prompt Library
                      </a>
                    </li>
                  )}
                  {Boolean(perms.tasklibraryReader) && (
                    <li>
                      <a
                        className={isActive('/Admin/TaskLibrary') ? 'active' : ''}
                        href='#/Admin/TaskLibrary'
                      >
                        Task Library
                      </a>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* ---- Messages with badge ---- */}
            {showMessages && messageCount > 0 && (
              <li>
                <a
                  className={`${isActive('/MessageDashboard') ? 'active' : ''}${messageCount ? ' top-right-badge' : ''}`}
                  data-badge={String(messageCount)}
                  href='#/MessageDashboard'
                >
                  Messages
                </a>
              </li>
            )}
            {showMessages && !messageCount && (
              <li>
                <a
                  className={isActive('/MessageDashboard') ? 'active' : ''}
                  href='#/MessageDashboard'
                >
                  Messages
                </a>
              </li>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
}
